import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, DIMENSION_META } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProgressRing from "@/components/ProgressRing";
import { ArrowRight, BookOpen, GitBranch, Flame, ClipboardCheck } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/dashboard/personal").then((r) => setData(r.data)); }, []);

  const hasDiag = !!data?.latest_diagnostic;
  const maturity = hasDiag
    ? Math.round(Object.values(data.latest_diagnostic.scores).reduce((a, b) => a + b, 0) / 4)
    : 0;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 fade-up">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Welcome back</div>
          <h1 className="font-serif text-4xl text-[#0B1E36]">Hi, {user?.name?.split(" ")[0]}.</h1>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-500 uppercase tracking-widest">Practice streak</div>
          <div className="font-serif text-3xl text-[#EC4899] flex items-center gap-2 justify-end mt-1">
            <Flame className="h-6 w-6" /> {data?.user?.streak || 0}
          </div>
        </div>
      </div>

      {!hasDiag && (
        <Link to="/app/diagnostic" data-testid="cta-diagnostic" className="block mb-10 group">
          <div className="bg-[#0B1E36] text-white rounded-2xl p-10 flex items-center justify-between card-lift">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#C9A961] mb-2">Start here</div>
              <div className="font-serif text-3xl mb-2">Take your EQ leadership baseline</div>
              <div className="text-slate-300 text-sm max-w-xl">20 questions, ~5 minutes. Your growth plan is built from this.</div>
            </div>
            <div className="h-16 w-16 rounded-full bg-[#EC4899] grid place-items-center group-hover:scale-105 transition-transform">
              <ArrowRight className="h-6 w-6 text-white" />
            </div>
          </div>
        </Link>
      )}

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-1 bg-white p-8 rounded-2xl border border-slate-200">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-6">Your Future Counts Index</div>
          <div className="flex flex-col items-center">
            <ProgressRing value={maturity} size={200} color="#EC4899" label="of 100" testId="maturity-ring" />
            <div className="mt-4 text-center">
              <div className="font-serif text-lg text-[#0B1E36]">{hasDiag ? "Baseline set" : "Awaiting baseline"}</div>
              <div className="text-xs text-slate-500 mt-1">{hasDiag ? "Retake quarterly for trend" : "Take the diagnostic"}</div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 bg-white p-8 rounded-2xl border border-slate-200">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-6">Dimension breakdown</div>
          <div className="grid grid-cols-2 gap-6">
            {Object.entries(DIMENSION_META).map(([k, meta]) => {
              const val = data?.latest_diagnostic?.scores?.[k] || 0;
              return (
                <div key={k} data-testid={`dim-${k}`}>
                  <div className="flex items-baseline justify-between mb-2">
                    <div className="font-semibold text-[#0B1E36] text-sm">{meta.label}</div>
                    <div className="font-serif text-2xl text-[#0B1E36]">{Math.round(val)}</div>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${val}%`, background: meta.accent }} />
                  </div>
                </div>
              );
            })}
          </div>
          {hasDiag && (
            <Link to="/app/diagnostic/results" data-testid="view-results" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#EC4899] hover:underline">
              View full growth plan <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <Link to="/app/lessons" data-testid="quick-lessons" className="bg-white p-8 rounded-2xl border border-slate-200 card-lift">
          <div className="h-11 w-11 rounded-xl bg-[#C9A961]/15 grid place-items-center mb-4"><BookOpen className="h-5 w-5 text-[#C9A961]" /></div>
          <div className="font-serif text-xl text-[#0B1E36] mb-1">Microlearning</div>
          <div className="text-sm text-slate-600">{data?.lessons_completed || 0} lessons completed</div>
        </Link>
        <Link to="/app/scenarios" data-testid="quick-scenarios" className="bg-white p-8 rounded-2xl border border-slate-200 card-lift">
          <div className="h-11 w-11 rounded-xl bg-[#EC4899]/15 grid place-items-center mb-4"><GitBranch className="h-5 w-5 text-[#EC4899]" /></div>
          <div className="font-serif text-xl text-[#0B1E36] mb-1">Simulations</div>
          <div className="text-sm text-slate-600">{data?.scenarios_completed || 0} scenarios run</div>
        </Link>
        <Link to="/app/diagnostic" data-testid="quick-retake" className="bg-white p-8 rounded-2xl border border-slate-200 card-lift">
          <div className="h-11 w-11 rounded-xl bg-[#0B1E36]/10 grid place-items-center mb-4"><ClipboardCheck className="h-5 w-5 text-[#0B1E36]" /></div>
          <div className="font-serif text-xl text-[#0B1E36] mb-1">{hasDiag ? "Retake diagnostic" : "Take diagnostic"}</div>
          <div className="text-sm text-slate-600">Recommended every quarter</div>
        </Link>
      </div>
    </div>
  );
}
